import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, makeStyles, tokens, Tooltip } from '@fluentui/react-components';
import {
  ChevronRight16Regular,
  Dismiss16Regular,
  Link16Regular,
  Sparkle16Filled,
} from '@fluentui/react-icons';
import LoadingIcon from './LoadingIcon';
import { AnalysisResult, ApplyOperation, Reference } from '../../services/analysis';
import { EditType, getRedliningEditTypes } from '../../services/editTypes';

interface SuggestionListProps {
  loading: boolean;
  iterating: boolean;
  activeGerund: string | null;
  result: AnalysisResult | null;
  lockedOperation: ApplyOperation;
  onApply: (rewrite: string, operation?: ApplyOperation) => void;
  onDismiss: () => void;
  onIterate?: (editType: EditType) => void;
  onScrollToReference?: (snippet: string) => void;
}

const useStyles = makeStyles({
  root: {
    marginTop: '12px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  card: {
    position: 'relative',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  dismiss: {
    position: 'absolute',
    top: '4px',
    right: '4px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '8px',
    paddingRight: '24px',
  },
  editLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  opBadge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: '8px',
  },

  // ---- References block (between title and rewrite) ----
  referencesBox: {
    padding: '8px 10px',
    marginBottom: '10px',
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  referencesLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: '4px',
  },
  referencesList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  refLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    padding: '2px 0',
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorBrandForeground1,
    textAlign: 'left',
    ':hover': { textDecoration: 'underline' },
  },

  // ---- Rewrite box ----
  rewriteWrapper: {
    position: 'relative',
    marginBottom: '10px',
  },
  rewrite: {
    fontSize: tokens.fontSizeBase300,
    whiteSpace: 'pre-wrap',
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '8px',
    borderRadius: tokens.borderRadiusSmall,
  },
  // Gradient applied to the TEXT itself via background-clip.
  iteratingText: {
    backgroundImage:
      'linear-gradient(90deg, #ec4899, #d946ef, #a855f7, #6366f1, #3b82f6, #1014f5, #6366f1, #a855f7, #ec4899)',
    backgroundSize: '300% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
    fontWeight: tokens.fontWeightSemibold,
    animationName: {
      '0%': { backgroundPosition: '0% 50%' },
      '100%': { backgroundPosition: '300% 50%' },
    },
    animationDuration: '2.5s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
  },
  iteratingBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '6px',
    marginBottom: '4px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: '#1014f5',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },

  // ---- Mini refine palette ----
  miniPaletteLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: '4px',
  },
  miniPalette: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginBottom: '10px',
  },
  miniChip: {
    minWidth: 'auto',
    minHeight: '22px',
    paddingLeft: '8px',
    paddingRight: '8px',
    fontSize: tokens.fontSizeBase100,
    lineHeight: '1.2',
  },

  // ---- What changed toggle ----
  rationaleToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    padding: '2px 0',
    marginBottom: '4px',
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  rationaleChevron: {
    transition: 'transform 0.12s ease',
    color: tokens.colorNeutralForeground3,
  },
  rationaleChevronOpen: {
    transform: 'rotate(90deg)',
  },
  rationale: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    marginBottom: '10px',
  },
});

function opLabel(operation: ApplyOperation): string {
  switch (operation) {
    case 'insert_before':
      return 'inserts before selection';
    case 'insert_after':
      return 'inserts after selection';
    default:
      return '';
  }
}

function applyButtonLabel(operation: ApplyOperation): string {
  switch (operation) {
    case 'insert_before':
      return 'Insert before';
    case 'insert_after':
      return 'Insert after';
    default:
      return 'Insert';
  }
}

const SuggestionList: React.FC<SuggestionListProps> = ({
  loading,
  iterating,
  activeGerund,
  result,
  lockedOperation,
  onApply,
  onDismiss,
  onIterate,
  onScrollToReference,
}) => {
  const styles = useStyles();
  const [rationaleOpen, setRationaleOpen] = useState(false);

  useEffect(() => {
    setRationaleOpen(false);
  }, [result]);

  if (loading && !iterating) {
    return (
      <div className={styles.root}>
        <div className={styles.loading}>
          <LoadingIcon size={24} />
          <span>{activeGerund ? `${activeGerund}…` : 'Analyzing…'}</span>
        </div>
      </div>
    );
  }

  if (!result || result.suggestions.length === 0) return null;

  const s = result.suggestions[0];
  const operation = lockedOperation;
  const opHint = opLabel(operation);
  const references: Reference[] = s.references ?? [];
  const controlsDisabled = iterating;

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <Button
          className={styles.dismiss}
          appearance="subtle"
          icon={<Dismiss16Regular />}
          size="small"
          aria-label="Dismiss suggestion"
          onClick={onDismiss}
          disabled={iterating}
        />
        <div className={styles.header}>
          <span className={styles.editLabel}>{result.editType.label}</span>
          {opHint && <span className={styles.opBadge}>· {opHint}</span>}
        </div>
        <div className={styles.title}>{s.title}</div>

        {references.length > 0 && (
          <div className={styles.referencesBox}>
            <div className={styles.referencesLabel}>Similar content exists in this document</div>
            <ul className={styles.referencesList}>
              {references.map((ref, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className={styles.refLink}
                    onClick={() => onScrollToReference?.(ref.snippet)}
                    disabled={!onScrollToReference}
                  >
                    <Link16Regular />
                    <span>{ref.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {s.rewrite && (
          <div className={styles.rewriteWrapper}>
            <div className={styles.rewrite}>
              <span className={iterating ? styles.iteratingText : undefined}>{s.rewrite}</span>
            </div>
          </div>
        )}

        {iterating && activeGerund && (
          <div className={styles.iteratingBadge}>
            <Sparkle16Filled /> <span>{activeGerund}…</span>
          </div>
        )}

        {s.rewrite && onIterate && (
          <>
            <div className={styles.miniPaletteLabel}>Refine</div>
            <div className={styles.miniPalette}>
              {getRedliningEditTypes().map(et => (
                <Tooltip key={et.id} content={et.description} relationship="description">
                  <Button
                    className={styles.miniChip}
                    size="small"
                    appearance="secondary"
                    disabled={controlsDisabled}
                    onClick={() => onIterate(et)}
                  >
                    {et.label}
                  </Button>
                </Tooltip>
              ))}
            </div>
          </>
        )}

        <button
          type="button"
          className={styles.rationaleToggle}
          onClick={() => setRationaleOpen(v => !v)}
          aria-expanded={rationaleOpen}
        >
          <ChevronRight16Regular
            className={`${styles.rationaleChevron} ${rationaleOpen ? styles.rationaleChevronOpen : ''}`}
          />
          <span>What changed</span>
        </button>
        {rationaleOpen && <div className={styles.rationale}>{s.rationale}</div>}

        {s.rewrite && (
          <Button
            size="small"
            appearance="primary"
            disabled={controlsDisabled}
            onClick={() => onApply(s.rewrite!, operation)}
          >
            {applyButtonLabel(operation)}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SuggestionList;
